import { useRef, useState } from "react";
import { FilterOption } from "../filterPopover/FilterPopover.types.ts";
import FilterBarValue from "./FilterBarValue.tsx";
import { IdNamePair } from "../../../customLists/customListView/CustomListView.types.ts";
import { PopperPlacementType } from "@mui/material/Popper/BasePopper.types";
import { hasFilterValue, isFilterChipDisplayed, nextPinnedFilterIds } from "./filterPinning.ts";

export interface FilterValueChipsProps {
    filterOptions: Array<FilterOption>;
    filterBarData: Record<string, IdNamePair[]>;
    onSave: (updatedFilterOptions: Array<FilterOption>, newFilterBarData: Record<string, IdNamePair[]>) => void;
    onRemove: (updatedFilterOptions: Array<FilterOption>) => void;
    popoverPlacement?: PopperPlacementType;
    // See FilterBar - pinning is opt-in per caller.
    pinnedFilterIds?: Array<string>;
    onPinnedFilterIdsChange?: (ids: Array<string> | undefined) => void;
}

function FilterValueChips({
    filterOptions,
    filterBarData,
    onSave,
    onRemove,
    popoverPlacement,
    pinnedFilterIds,
    onPinnedFilterIdsChange,
}: FilterValueChipsProps) {
    const [openSubmenuId, setOpenSubmenuId] = useState<string | undefined>(undefined);
    // Chips render in the order they were first activated, so editing a value
    // doesn't reshuffle existing chips next to it.
    const displayOrderRef = useRef<string[]>([]);

    const optionsWithData = filterOptions.filter((x) => isFilterChipDisplayed(x, pinnedFilterIds));
    const currentActiveIds = optionsWithData.map((x) => x.id);
    displayOrderRef.current = displayOrderRef.current.filter((id) => currentActiveIds.includes(id));
    currentActiveIds.forEach((id) => {
        if (!displayOrderRef.current.includes(id)) {
            displayOrderRef.current.push(id);
        }
    });
    const optionsToDisplay = displayOrderRef.current.map((id) => optionsWithData.find((x) => x.id === id)).filter((x): x is FilterOption => x !== undefined);

    function handleSave(updatedOption: FilterOption, newFilterBarData: Record<string, IdNamePair[]>) {
        const updatedOptions = filterOptions.map((o) => (o.id === updatedOption.id ? updatedOption : o));
        onSave(updatedOptions, newFilterBarData);
        onPinnedFilterIdsChange?.(nextPinnedFilterIds(filterOptions, pinnedFilterIds, updatedOptions));
        setOpenSubmenuId(undefined);
    }

    function handleRemove(optionId: string) {
        const updatedOptions = filterOptions.map((o) => (o.id === optionId ? { ...o, value: null } : o));
        onRemove(updatedOptions);
        onPinnedFilterIdsChange?.(nextPinnedFilterIds(filterOptions, pinnedFilterIds, updatedOptions, optionId));
        setOpenSubmenuId(undefined);
    }

    return (
        <>
            {optionsToDisplay.map((option, index) => (
                <FilterBarValue
                    key={option.id + index}
                    filterOption={option}
                    filterBarData={filterBarData}
                    isOpen={openSubmenuId === option.id}
                    onToggleSubmenu={setOpenSubmenuId}
                    onRemoveOptionClick={() => handleRemove(option.id)}
                    onSave={handleSave}
                    popoverPlacement={popoverPlacement}
                    isPinned={!hasFilterValue(option.value)}
                />
            ))}
        </>
    );
}

export default FilterValueChips;
